import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import * as opcClientApi from '../../api/app/opcClientApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { SelectBoxAutocomplete } from '../../components/core/SelectBox/SelectBoxAutocomplete';
import { useTranslation } from '../../contexts/translation';
import { MonitoredProcessDataInputs } from '../../types/app/monitoredProcessData.types';
import { OpcClient } from '../../types/app/opcClient.types';
import { dictionary } from '../../utils/core/dictionary';
interface Props {
  onSubmit: SubmitHandler<MonitoredProcessDataInputs>;
  defaultValues?: MonitoredProcessDataInputs;
  onBack: any;
}

export function MonitoredProcessDataForm({ onSubmit, defaultValues, onBack }: Props) {
  const { t } = useTranslation();
  const [endpointAddress, setEndpointAddress] = useState<string>('');
  const [nodeList, setNodeList] = useState<string[]>();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm<MonitoredProcessDataInputs>();

  const {
    data: opcClientList,
    isLoading,
    isError,
  } = useQuery<OpcClient[]>('opcClientList', opcClientApi.getOpcClientList);

  useQuery<string[]>(['nodeList', endpointAddress], () => opcClientApi.browse(endpointAddress), {
    enabled: endpointAddress.length > 0,
    onSuccess: (res) => {
      let tmpNodeList: string[] = [];
      res.map((r: string) => {
        tmpNodeList = [...tmpNodeList, r];
      });
      setNodeList(tmpNodeList);
    },
  });

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!defaultValues?.opcClientId && opcClientList) return;
    let tmpC = opcClientList?.find((x: any) => x.id === defaultValues?.opcClientId);
    if (tmpC) setEndpointAddress(tmpC.endpointAddress);
  }, [defaultValues && defaultValues?.opcClientId]);

  useEffect(() => {
    if (defaultValues) return;
    setValue('name', 'PD00');
    setValue('influxValueName', 'pd00');
    setValue('nodeId', 'ns=2;s=');
  }, []);

  if (isError) return <ApiError />;
  if (isLoading || !opcClientList) return <Loader />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Name"
              {...register('name', {
                required: `Name ${dictionary.IS_REQUIRED}`,
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Data type</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Data type"
                defaultValue={defaultValues?.dataType}
                {...register('dataType', { required: 'Typ ' + dictionary.IS_REQUIRED })}
              >
                <MenuItem value={'Float'}>Float</MenuItem>
                <MenuItem value={'Integer'}>Integer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <SelectBoxAutocomplete
              isRequired
              label={dictionary.OPCCLIENT}
              items={opcClientList}
              valueKey={'id'}
              labelKey={'instanceName'}
              onChange={(e: any) => {
                let tmpC = opcClientList.find((x: OpcClient) => x.id === e);
                if (tmpC) setEndpointAddress(tmpC?.endpointAddress);
              }}
              controllerProps={{
                name: 'opcClientId',
                control: control,
                rules: { required: 'OPC client ' + dictionary.IS_REQUIRED },
              }}
              errors={errors}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Influx value name"
              {...register('influxValueName', {
                required: `Influx valule name ${dictionary.IS_REQUIRED}`,
              })}
            />
          </Grid>
          <Grid item xs={12}>
            {!nodeList && (
              <TextField
                fullWidth
                required
                label="nodeId"
                {...register('nodeId', { required: `Node id ${dictionary.IS_REQUIRED}` })}
              />
            )}
            {nodeList && (
              <Autocomplete
                id="free-solo-demo"
                freeSolo
                value={defaultValues && defaultValues.nodeId ? defaultValues.nodeId : 'ns=2;s='}
                options={nodeList.map((node) => node)}
                onInputChange={(e, data) => setValue('nodeId', data)}
                renderInput={(params) => <TextField {...params} label="nodeId" />}
              />
            )}
          </Grid>
          <Grid item>
            <Stack spacing={2} direction="row">
              <Button type="button" variant="outlined" onClick={onBack}>
                {t('general.action-buttons.back')}
              </Button>
              <Button type="submit" variant="contained" color="success">
                {t('general.action-buttons.save')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
